import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, DataGrid, KeenIcon } from '@/components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useLayout } from '@/providers';
import {
  useDeleteListValueMutation,
  useGetAdminListValuesQuery,
} from '@/services/ListValueService';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AdminListValue,
  ListValueStatus,
  ManagedListType,
} from '@/types/list-value.types';
import {
  DEFAULT_MANAGED_LIST_TYPE,
  getBadgeClass,
  getStatusLabel,
  isListValueManagementType,
  LIST_TYPE_CONFIG,
  LIST_VALUE_MANAGEMENT_TYPES,
  supportsDisplayOrder,
} from './listValueManagement.shared';

const ListValueManagement = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const selectedType = searchParams.get('type');
  const [activeType, setActiveType] = useState<ManagedListType>(
    isListValueManagementType(selectedType)
      ? selectedType
      : DEFAULT_MANAGED_LIST_TYPE
  );
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ListValueStatus>(
    'all'
  );
  const [deleteTarget, setDeleteTarget] = useState<AdminListValue | null>(null);

  useEffect(() => {
    if (
      isListValueManagementType(selectedType) &&
      selectedType !== activeType
    ) {
      setActiveType(selectedType);
      return;
    }

    if (!selectedType || !isListValueManagementType(selectedType)) {
      setSearchParams({ type: activeType }, { replace: true });
    }
  }, [activeType, selectedType, setSearchParams]);

  const canCreate = hasPermission('list_value_management_create');
  const canUpdate = hasPermission('list_value_management_update');
  const canDelete = hasPermission('list_value_management_delete');

  const {
    data: activeValues = [],
    isLoading,
    isError,
  } = useGetAdminListValuesQuery({
    type: activeType,
  });

  const [deleteListValue, { isLoading: isDeleting }] =
    useDeleteListValueMutation();

  const filteredValues = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return activeValues.filter((item) => {
      const matchesStatus =
        statusFilter === 'all' ? true : item.status === statusFilter;

      if (!matchesStatus) return false;
      if (!normalizedSearch) return true;

      return [
        item.key,
        item.label_en,
        item.label_fi,
        item.label_sv,
        item.label_de,
        item.category,
        item.icon_id,
        item.notes,
        item.linked_jira_issue,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch)
        );
    });
  }, [activeValues, search, statusFilter]);

  const handleTypeChange = (value: ManagedListType) => {
    setActiveType(value);
    setSearch('');
    setStatusFilter('all');
    setSearchParams({ type: value }, { replace: true });
  };

  const currentIndex = LIST_VALUE_MANAGEMENT_TYPES.indexOf(activeType);
  const goPrevTab = () => {
    const prev =
      LIST_VALUE_MANAGEMENT_TYPES[
        (currentIndex - 1 + LIST_VALUE_MANAGEMENT_TYPES.length) %
          LIST_VALUE_MANAGEMENT_TYPES.length
      ];
    handleTypeChange(prev);
  };
  const goNextTab = () => {
    const next =
      LIST_VALUE_MANAGEMENT_TYPES[
        (currentIndex + 1) % LIST_VALUE_MANAGEMENT_TYPES.length
      ];
    handleTypeChange(next);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteListValue({
        type: deleteTarget.type,
        id: String(deleteTarget.id),
      }).unwrap();
      enqueueSnackbar('List value marked deprecated successfully.', {
        variant: 'success',
      });
      setDeleteTarget(null);
    } catch {
      enqueueSnackbar('Failed to delete list value.', { variant: 'error' });
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'key',
        header: 'Key',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <div className="font-medium text-gray-800">{row.original.key}</div>
        ),
      },
      {
        accessorKey: 'label_en',
        header: 'English',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className="text-gray-700">{row.original.label_en || '-'}</span>
        ),
      },
      {
        accessorKey: 'label_fi',
        header: 'Finnish',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className="text-gray-700">{row.original.label_fi || '-'}</span>
        ),
      },
      {
        accessorKey: 'label_sv',
        header: 'Swedish',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className="text-gray-700">{row.original.label_sv || '-'}</span>
        ),
      },
      {
        accessorKey: 'label_de',
        header: 'German',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className="text-gray-700">{row.original.label_de || '-'}</span>
        ),
      },
    ];

    const categoryColumn =
      activeType === ManagedListType.POI_SUBTYPE
        ? [
            {
              accessorKey: 'category',
              header: 'POI Category',
              cell: ({ row }: { row: { original: AdminListValue } }) => (
                <span className="text-gray-700">
                  {row.original.category || row.original.category_id || '-'}
                </span>
              ),
            },
          ]
        : activeType === ManagedListType.SERVICE_SUB_CATEGORY
          ? [
              {
                accessorKey: 'category',
                header: 'Service Category',
                cell: ({ row }: { row: { original: AdminListValue } }) => (
                  <span className="text-gray-700">
                    {row.original.category || '-'}
                  </span>
                ),
              },
            ]
          : [];

    return [
      ...baseColumns,
      ...categoryColumn,
      ...(supportsDisplayOrder(activeType)
        ? [
            {
              accessorKey: 'display_order',
              header: 'Display order',
              cell: ({ row }: { row: { original: AdminListValue } }) => (
                <span className="text-gray-700">
                  {row.original.display_order ?? '-'}
                </span>
              ),
            },
          ]
        : []),
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className={getBadgeClass(row.original.status)}>
            {getStatusLabel(row.original.status)}
          </span>
        ),
      },
      {
        accessorKey: 'linked_jira_issue',
        header: 'Jira',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className="text-gray-700">
            {row.original.linked_jira_issue || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <span className="line-clamp-2 max-w-xs text-gray-600">
            {row.original.notes || row.original.description || '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: AdminListValue } }) => (
          <div className="flex items-center gap-2">
            {canUpdate && (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light"
                onClick={() =>
                  navigate(
                    `/list-value-management/edit/${row.original.type}/${row.original.id}`
                  )
                }
                title="Edit list value"
              >
                <KeenIcon icon="pencil" />
              </button>
            )}
            {canDelete &&
              row.original.status !== ListValueStatus.DEPRECATED && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light"
                  onClick={() => setDeleteTarget(row.original)}
                  title="Mark deprecated"
                >
                  <KeenIcon icon="trash" />
                </button>
              )}
          </div>
        ),
      },
    ];
  }, [activeType, canDelete, canUpdate, navigate]);

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="List Value Management" />
              <ToolbarDescription>
                Maintain dynamic list values.
              </ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 flex-col gap-3 min-w-0">
              <div className="list-value-management-header border-b border-gray-200">
                <nav
                  className="-mb-px hidden md:flex space-x-6 overflow-x-auto"
                  role="tablist"
                  aria-label="List value type tabs"
                >
                  {LIST_VALUE_MANAGEMENT_TYPES.map((type) => (
                    <button
                      key={type}
                      type="button"
                      role="tab"
                      aria-selected={activeType === type}
                      onClick={() => handleTypeChange(type)}
                      className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${
                        activeType === type
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {LIST_TYPE_CONFIG[type].title}
                    </button>
                  ))}
                </nav>
                <div
                  className="list-value-mobile-nav flex md:hidden items-center justify-between"
                  aria-label="Tabs mobile"
                >
                  <button
                    type="button"
                    className="nav-arrow prev"
                    onClick={goPrevTab}
                    aria-label="Previous tab"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="current-tab" aria-current="page">
                    {LIST_TYPE_CONFIG[activeType].title}
                  </div>
                  <button
                    type="button"
                    className="nav-arrow next"
                    onClick={goNextTab}
                    aria-label="Next tab"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {LIST_TYPE_CONFIG[activeType].blurb}
              </p>
            </div>
          </div>

          <div className="card card-body space-y-4 flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="relative">
                  <KeenIcon
                    icon="magnifier"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search by key or label..."
                    className="input input-sm h-10 w-full sm:w-72 pl-10"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className="input input-sm h-10 w-full sm:w-48"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as 'all' | ListValueStatus)
                  }
                >
                  <option value="all">All statuses</option>
                  {Object.values(ListValueStatus).map((status) => (
                    <option key={status} value={status}>
                      {getStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-500 flex justify-between sm:justify-start gap-2 items-center">
                {filteredValues.length} value
                {filteredValues.length === 1 ? '' : 's'}
                {canCreate && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() =>
                      navigate(`/list-value-management/create/${activeType}`)
                    }
                  >
                    Add Value
                  </button>
                )}
              </div>
            </div>

            {isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                Failed to load list values. Please try again.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={filteredValues}
                loading={isLoading}
                rowSelect={false}
                messages={{ empty: LIST_TYPE_CONFIG[activeType].emptyLabel }}
                pagination={{ page: 0, size: 10, more: false }}
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        message={
          <span>
            Mark <strong>{deleteTarget?.key}</strong> as deprecated? This value
            will no longer appear in approved frontend lists.
          </span>
        }
        confirmButtonText={isDeleting ? 'Updating...' : 'Mark Deprecated'}
        confirmButtonVariant="destructive"
        disabled={isDeleting}
        isLoading={isDeleting}
      />
    </Fragment>
  );
};

export { ListValueManagement };
